import React, { useEffect, useState, useRef } from 'react';
import { 
  Badge, 
  Button, 
  Col, 
  Container, 
  Form, 
  Modal, 
  Row, 
  Spinner, 
  Table,
  Card,
  InputGroup
} from 'react-bootstrap';
import axios from '../../api/axios';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import Header from '../../layouts/Header';
import ReusableTable from '../../components/ReusableTable';
import { Send, Paperclip, Download } from 'lucide-react';

const CustomerSupportMgt = () => {
    const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
    const [skin, setSkin] = useState(currentSkin);

    const accessToken = localStorage.getItem('authToken');
    const [ticketList, setTicketList] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState("OPEN");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [loadingRowId, setLoadingRowId] = useState(null);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showModal, setShowModal] = useState(false);
    const [viewModal, setViewModal] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState({});
    const [conversation, setConversation] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const messagesEndRef = useRef(null);

    const columns = [
        {
            header: "Sr.No",
            accessor: "serialNumber",
            render: (value) => <span>{value}</span>,
        },          
        {
            header: "Ticket No",
            accessor: "ticket_no",
            render: (value) => <div className="fs-7 fw-semibold">{value || "--------"}</div>,
        },
        {
            header: "Raised On",
            accessor: "createdAt",
            render: (value) =>
                value ? (
                    <div className="fs-7">
                        {new Date(value).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "numeric",
                            day: "numeric",
                        })}
                        <br />
                        {new Date(value).toLocaleTimeString("en-US", {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                        })}
                    </div>
                ) : (
                "--------"
            ),
        },
        {
            header: "Customer",
            accessor: null,
            render: (_, row) => (
                <div>
                    <div className="fs-7 fw-semibold text-primary">
                        {row?.user?.name || "--------"}
                    </div>
                    <div className="fs-8 text-muted">
                        {row?.user?.email?.split('_deleted_')[0] || "--------"}
                    </div>
                </div>
            ),
        },
        {
            header: "Support Type",
            accessor: "support_type",
            render: (value) => (
                <Badge bg="secondary" className="fs-8">
                    {value || "--------"}
                </Badge>
            ),
        },
        {
            header: "Subject",
            accessor: "subject",
            render: (value) => (
                <div className="fs-7 fw-medium">{value ? value.toUpperCase() : "--------"}</div>
            ),
        },
        {
            header: "Status",
            accessor: "status",
            render: (value) => (
                <Badge 
                    bg={value === "OPEN" ? "success" : "danger"} 
                    className="fs-8"
                >
                    {value}
                </Badge>
            ),
        },
        {
            header: "Last Activity",
            accessor: "updatedAt",
            render: (value) =>
                value ? (
                    <div className="fs-8 text-muted">
                        {new Date(value).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                        })}
                    </div>
                ) : (
                "--------"
            ),
        },
        {
            header: "Actions",
            accessor: null,
            render: (_, row) => (
                <div className="d-flex align-items-center gap-2 w-100">
                    <Button
                        size="sm"
                        variant={row?.status === "OPEN" ? "outline-danger" : "outline-success"}
                        className="fw-semibold"
                        style={{
                            borderRadius: "8px",
                            fontSize: "12px",
                        }}
                        onClick={() => handleUpdateStatus(row)}
                        disabled={loadingRowId === row._id}
                    >
                        {loadingRowId === row._id ? (
                            <Spinner size="sm" animation="border" />
                        ) : row?.status === "OPEN" ? (
                            "Close"
                        ) : (
                            "Reopen"
                        )}
                    </Button>
        
                    <Button
                        size="sm"
                        variant="primary"
                        className="fw-semibold"
                        style={{
                            borderRadius: "8px",
                            fontSize: "12px",
                        }}
                        onClick={() => handleViewDetails(row)}
                    >
                        View Chat
                    </Button>
                </div>
            ),
        },
    ];

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [conversation]);

    const handleSubmit = async (e) => {
        const adjustedStartDate = new Date(startDate?.setHours(23, 59, 59, 999));
        const adjustedEndDate = new Date(endDate?.setHours(23, 59, 59, 999));

        setLoading(true);
        await getTickets(selectedStatus, adjustedStartDate, adjustedEndDate);
        toast.success("Data Fetched Successfully", {
            position: "top-right",
            autoClose: 3000,
            theme: "colored",
        });
        setLoading(false);
    }

    const getTickets = async (status, startDate, endDate) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };

            const updatedStartDate = startDate ? startDate.toISOString().split("T")[0] : null;
            const updatedEndDate = endDate ? endDate.toISOString().split("T")[0] : null;

            let url = `customer-support/get-tickets?`

            if (status && status !== 'all') {
                url += `status=${status}&`
            }

            if (updatedStartDate) {
                url += `startDate=${updatedStartDate}&`
            }

            if (updatedEndDate) {
                url += `endDate=${updatedEndDate}&`
            }

            url = url.endsWith('&') ? url.slice(0, -1) : url;

            const response = await axios.get(url, { headers });

            const tableDataWithIndex = response?.data?.data?.tickets?.map((item, idx) => ({
                ...item,
                serialNumber: idx + 1,
            }));

            setTicketList(tableDataWithIndex);
            setFilteredData(tableDataWithIndex);
        } catch (error) {
            if (error.response && error.response?.data?.status === false) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            } else if (error.response && error.response?.data?.status === 404) {
                let errorMessage = error.response.data.error;
                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        }
    };

    const getTicketConversation = async (ticketId) => {
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`
            };
            const response = await axios.get(`customer-support/${ticketId}/conversation`, { headers });
            console.log("Response :", response);
            
            setConversation(response?.data?.data?.messages || []);
        } catch (error) {
            console.log("Error In Getting Messages :", error);
            toast.error("Failed to load conversation", {
                position: "top-right",
                autoClose: 3000,
                theme: "colored",
            });
        }
    };

    const sendMessage = async () => {
        if (!newMessage.trim() && attachments.length === 0) return;

        setSendingMessage(true);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
                'Content-Type': 'multipart/form-data'
            };

            const formData = new FormData();
            // formData.append('ticketId', selectedTicket._id);
            formData.append('message', newMessage);
            // formData.append('senderType', 'admin');
            // formData.append('attachment', );
            attachments.forEach((file, index) => {
                formData.append(`attachment`, file);
            });

            const response = await axios.post(`customer-support/${selectedTicket?._id}/conversation`, formData, { headers });

            if (response?.data?.status === true) {
                setNewMessage('');
                setAttachments([]);
                await getTicketConversation(selectedTicket._id);
                toast.success("Message sent successfully", {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "light",
                });
            }
        } catch (error) {
            console.log("Error sending message:", error);
            toast.error("Failed to send message", {
                position: "top-right",
                autoClose: 3000,
                theme: "light",
            });
        } finally {
            setSendingMessage(false);
        }
    };

    const handleFileAttach = (e) => {
        // const files = e.target.files;
        // setAttachments(files);
        const files = Array.from(e.target.files);
        setAttachments(prev => [...prev, ...files]);
    };

    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    const handleSearchChange = (e) => {
        setSearchText(e.target.value);
    }

    const handleEndDateChange = (date) => {
        if (date && startDate && date < startDate) {
            setError("End date cannot be earlier than start date")
        } else {
            setError("");
            setEndDate(date);
        }
    }

    const handleUpdateStatus = async (data) => {
        const newStatus = data?.status === "OPEN" ? "CLOSED" : "OPEN";
        setLoadingRowId(data?._id);
        try {
            const headers = {
                Authorization: `Bearer ${accessToken}`,
            };
            const response = await axios.put(`customer-support/user-tickets`, {
                _id: data?._id,
                status: newStatus
            }, { headers });

            if (response?.data?.status === true) {
                toast.success(response?.data?.data?.message, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                })
                getTickets(selectedStatus, startDate, endDate)
            }
        } catch (error) {
            if (error?.response) {
                let errorMessage = '';
                if (error.response?.status === 406) {
                    errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
                }
                else if (error.response?.status === 412) {
                    errorMessage = error?.response?.data?.data?.message;
                } else if (error.response?.status === 500) {
                    errorMessage = error?.response?.data?.error
                }

                toast.error(errorMessage, {
                    position: "top-right",
                    autoClose: 3000,
                    theme: "colored",
                });
            }
        } finally {
            setLoadingRowId(null);
        }
    };

    const handleViewDetails = async (data) => {
        console.log("View Ticket Data :", data);
        
        setSelectedTicket(data);
        setViewModal(true);
        await getTicketConversation(data._id);
    }

    const handleCloseViewModal = () => {
        setViewModal(false);
        setSelectedTicket({});
        setConversation([]);
        setNewMessage('');
        setAttachments([]);
    }

    const handlePageChange = (page) => {
        setPage(page);
    };
  
    const handleRowsPerPageChange = (rowsPerPage) => {
        setPage(1);
        setRowsPerPage(rowsPerPage);
    };

    const getPaginatedData = () => {
        const startIndex = (page - 1) * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredData.slice(startIndex, endIndex);
    };

    const formatMessageTime = (timestamp) => {
        return new Date(timestamp).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    useEffect(() => {
        const now = new Date();
        const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
        const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
        const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
        const end = new Date(currentDate.setHours(23, 59, 59, 999));

        setStartDate(start);
        setEndDate(end);
        getTickets(selectedStatus, start, end);
    }, []);

    useEffect(() => {
        const result = ticketList.filter((item) => {
            return (
                item?.user?.name?.toLowerCase().match(searchText.toLocaleLowerCase()) ||
                item?.support_type?.toLowerCase().match(searchText.toLocaleLowerCase()) ||
                item?.ticket_no?.toLowerCase().match(searchText.toLocaleLowerCase()) ||
                item?.subject?.toLowerCase().match(searchText.toLocaleLowerCase())
            )
        });
        setFilteredData(result)
    }, [searchText]);

    return (
        <React.Fragment>
            <Header onSkin={setSkin} />
            <div className="main main-app p-3 p-lg-4">
                <Container fluid className="shadow-sm bg-white py-3 rounded-3">
                    <Row className="mb-3">
                        <Col>
                            <h5 className="fw-bold text-primary">CUSTOMER SUPPORT MANAGEMENT</h5>
                            <p className="text-muted mb-0">Manage and respond to customer support tickets</p>
                        </Col>
                    </Row>

                    {/* Filters */}
                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body>
                            <Row className="g-3 align-items-end">
                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Status Filter</Form.Label>
                                        <Form.Select
                                            value={selectedStatus}
                                            onChange={(e) => setSelectedStatus(e.target.value)}
                                            className="border-0 shadow-sm"
                                        >
                                            <option value="all">All Tickets</option>
                                            <option value="OPEN">Open Tickets</option>
                                            <option value="CLOSED">Closed Tickets</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Start Date</Form.Label>
                                        <DatePicker
                                            selected={startDate}
                                            onChange={(date) => setStartDate(date)}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="Start Date"
                                            className="form-control border-0 shadow-sm"
                                        />
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">End Date</Form.Label>
                                        <DatePicker
                                            selected={endDate}
                                            onChange={handleEndDateChange}
                                            dateFormat="dd-MM-yyyy"
                                            placeholderText="End Date"
                                            className="form-control border-0 shadow-sm"
                                        />
                                        {error && <div className="text-danger small mt-1">{error}</div>}
                                    </Form.Group>
                                </Col>

                                <Col md={3}>
                                    <Form.Group>
                                        <Form.Label className="fw-semibold">Search Tickets</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Search by customer, ticket no, subject..."
                                            value={searchText}
                                            onChange={handleSearchChange}
                                            className="border-0 shadow-sm"
                                        />
                                    </Form.Group>
                                </Col>
                                
                                <Col md={2}>
                                    <Button 
                                        variant="primary" 
                                        className="w-100 fw-semibold"
                                        onClick={handleSubmit}
                                        disabled={loading}
                                    >
                                        {loading ? <Spinner size="sm" animation="border" /> : 'Apply Filters'}
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Tickets List */}
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-transparent border-0">
                            <h6 className="mb-0 fw-bold">Support Tickets</h6>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <ReusableTable
                                title=""
                                columns={columns}
                                data={getPaginatedData()}
                                rowKey="serialNumber"
                                loading={loading}
                                currentPage={page}
                                recordsPerPage={rowsPerPage}
                                onPageChange={handlePageChange}
                                totalPages={Math.ceil(filteredData.length / rowsPerPage)}
                                onRecordsPerPageChange={handleRowsPerPageChange}
                            />
                        </Card.Body>
                    </Card>
                </Container>

                {/* Ticket Chat Modal */}
                {/* Ticket Chat Modal */}
                <Modal
                    show={viewModal}
                    onHide={handleCloseViewModal}
                    size="xl"
                    centered
                    className="ticket-chat-modal"
                >
                    <Modal.Header closeButton className="bg-light">
                        <div className="w-100">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <Modal.Title className="fw-bold">Ticket #{selectedTicket?.ticket_no}</Modal.Title>
                                    <p className="mb-0 text-muted">{selectedTicket?.subject}</p>
                                </div>
                                <Badge 
                                    bg={selectedTicket?.status === "OPEN" ? "success" : "danger"}
                                    className="fs-6"
                                >
                                    {selectedTicket?.status}
                                </Badge>
                            </div>
                            <div className="mt-2">
                                <small className="text-muted">
                                    <strong>Customer:</strong> {selectedTicket?.user?.name} • 
                                    <strong> Type:</strong> {selectedTicket?.support_type} • 
                                    <strong> Created:</strong> {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleDateString() : ''}
                                </small>
                            </div>
                        </div>
                    </Modal.Header>
    
                    <Modal.Body className="p-0">
                        <div className="d-flex" style={{ height: '60vh' }}>
                            {/* Conversation Panel */}
                            <div className="flex-fill d-flex flex-column">
                                <div 
                                    className="flex-fill p-3 overflow-auto"
                                    style={{ maxHeight: '45vh', backgroundColor: '#f8f9fa' }}
                                >
                                    {conversation.length === 0 ? (
                                        <div className="text-center text-muted mt-5">
                                            <p>No messages yet. Start the conversation!</p>
                                        </div>
                                    ) : (
                                        conversation.map((message, index) => (
                                            <div
                                                key={message._id || index}
                                                className={`d-flex mb-3 ${
                                                    message.senderType === 'admin' 
                                                        ? 'justify-content-end' 
                                                        : 'justify-content-start'
                                                }`}
                                            >
                                                <div
                                                    className={`p-3 rounded-3 position-relative ${
                                                        message.senderType === 'admin'
                                                            ? 'bg-primary text-white'
                                                            : 'bg-light border'
                                                    }`}
                                                    style={{ maxWidth: '70%' }}
                                                >
                                                    <div className="message-content">
                                                        {message.message && (
                                                            <p className="mb-2">{message.message}</p>
                                                        )}
                                                        
                                                        {message?.attachments?.length > 0 && (
                                                            <div className="attachments">
                                                                {message?.attachments?.map((file, fileIndex) => (
                                                                    <div key={fileIndex} className="attachment-item mb-1">
                                                                        <a 
                                                                            href={file.url} 
                                                                            target="_blank" 
                                                                            rel="noopener noreferrer"
                                                                            className={`d-flex align-items-center ${
                                                                                message.senderType === 'admin' 
                                                                                    ? 'text-white' 
                                                                                    : 'text-primary'
                                                                            }`}
                                                                        >
                                                                            <Download size={14} className="me-1" />
                                                                            {file.name}
                                                                        </a>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <small 
                                                        className={`opacity-75 ${
                                                            message.senderType === 'admin' 
                                                                ? 'text-white-50' 
                                                                : 'text-muted'
                                                        }`}
                                                    >
                                                        {formatMessageTime(message.createdAt)}
                                                    </small>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Message Input Area */}
                                <div className="border-top p-3 bg-white">
                                    {attachments?.length > 0 && (
                                        <div className="mb-2">
                                            {attachments?.map((file, index) => (
                                                <Badge key={index} bg="light" text="dark" className="me-2 mb-1">
                                                    {file.name}
                                                    <button 
                                                        type="button" 
                                                        className="btn-close btn-close-sm ms-1"
                                                        onClick={() => removeAttachment(index)}
                                                        aria-label="Remove"
                                                    />
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                    
                                    <InputGroup>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            placeholder="Type your message..."
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    sendMessage();
                                                }
                                            }}
                                            className="border-0"
                                        />
                                        <InputGroup.Text className="bg-transparent border-0">
                                            <label className="btn btn-link p-0 me-2" title="Attach files">
                                                <Paperclip size={20} className="text-muted" />
                                                <input
                                                    type="file"
                                                    multiple
                                                    onChange={handleFileAttach}
                                                    style={{ display: 'none' }}
                                                />
                                            </label>
                                            <Button
                                                variant="primary"
                                                onClick={sendMessage}
                                                disabled={sendingMessage || (!newMessage.trim() && attachments?.length === 0)}
                                                className="rounded-circle"
                                                size="sm"
                                            >
                                                {sendingMessage ? (
                                                    <Spinner size="sm" animation="border" />
                                                ) : (
                                                    <Send size={16} />
                                                )}
                                            </Button>
                                        </InputGroup.Text>
                                    </InputGroup>
                                </div>
                            </div>

                            {/* Ticket Details Sidebar */}
                            <div className="border-start" style={{ width: '350px', overflowY: 'auto' }}>
                                <div className="p-3">
                                    <h6 className="fw-bold mb-3 border-bottom pb-2">Ticket Details</h6>
                                    
                                    {/* Customer Information */}
                                    <div className="mb-3">
                                        <h6 className="text-muted small fw-semibold mb-2">CUSTOMER INFORMATION</h6>
                                        <div className="bg-light p-2 rounded">
                                            <p className="mb-1 fw-semibold">{selectedTicket?.user?.name}</p>
                                            <p className="mb-1 text-muted small">{selectedTicket?.user?.email?.split('_deleted_')[0]}</p>
                                            <p className="mb-0 text-muted small">
                                                {selectedTicket?.user?.mobile?.includes("_deleted_")
                                                    ? `${selectedTicket?.user?.mobile?.split("_deleted_")[0]} [Deleted]`
                                                    : selectedTicket?.user?.mobile
                                                }
                                            </p>
                                        </div>
                                    </div>

                                    {/* Ticket Information */}
                                    <div className="mb-3">
                                        <h6 className="text-muted small fw-semibold mb-2">TICKET INFORMATION</h6>
                                        <div className="row g-2 small">
                                            <div className="col-6">
                                                <span className="text-muted">Type:</span>
                                            </div>
                                            <div className="col-6 text-end">
                                                <span className="fw-semibold">{selectedTicket?.support_type}</span>
                                            </div>
                                            
                                            <div className="col-6">
                                                <span className="text-muted">Subject:</span>
                                            </div>
                                            <div className="col-6 text-end">
                                                <span className="fw-semibold text-truncate d-inline-block" style={{maxWidth: '120px'}} title={selectedTicket?.subject}>
                                                    {selectedTicket?.subject}
                                                </span>
                                            </div>
                                            
                                            <div className="col-6">
                                                <span className="text-muted">Created:</span>
                                            </div>
                                            <div className="col-6 text-end">
                                                <span className="fw-semibold">
                                                    {selectedTicket?.createdAt ? new Date(selectedTicket.createdAt).toLocaleDateString() : ''}
                                                </span>
                                            </div>
                                            
                                            <div className="col-6">
                                                <span className="text-muted">Last Updated:</span>
                                            </div>
                                            <div className="col-6 text-end">
                                                <span className="fw-semibold">
                                                    {selectedTicket?.updatedAt ? new Date(selectedTicket.updatedAt).toLocaleDateString() : ''}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ticket Description */}
                                    <div className="mb-3">
                                        <h6 className="text-muted small fw-semibold mb-2">DESCRIPTION</h6>
                                        <div className="bg-light p-2 rounded small">
                                            {selectedTicket?.message}
                                        </div>
                                    </div>

                                    {/* Order Details Section - Only show if order exists */}
                                    {selectedTicket?.order && (
                                        <div className="mb-3">
                                            <h6 className="text-muted small fw-semibold mb-2">
                                                ORDER DETAILS 
                                                <Badge bg="info" className="ms-2"># {selectedTicket?.order?.order_sequence}{selectedTicket?.order?.order_no}</Badge>
                                            </h6>
                                            <div className="border rounded">
                                                {/* Order Header */}
                                                <div className="bg-light p-2 border-bottom">
                                                    <div className="d-flex justify-content-between align-items-center">
                                                        <span className="fw-semibold">Order # {selectedTicket?.order?.order_sequence}{selectedTicket?.order?.order_no}</span>
                                                        <Badge 
                                                            bg={
                                                                selectedTicket?.order?.orderStatus === 'CONFIRMED' ? 'success' :
                                                                selectedTicket?.order?.orderStatus === 'PENDING' ? 'warning' :
                                                                selectedTicket?.order?.orderStatus === 'CANCELLED' ? 'danger' : 'secondary'
                                                            }
                                                            className="fs-7"
                                                        >
                                                            {selectedTicket?.order?.orderStatus}
                                                        </Badge>
                                                    </div>
                                                    <small className="text-muted">
                                                        {selectedTicket?.order?.createdAt ? new Date(selectedTicket.order.createdAt).toLocaleString() : ''}
                                                    </small>
                                                </div>

                                                {/* Order Summary */}
                                                <div className="p-2">
                                                    <div className="row g-1 small">
                                                        <div className="col-6">
                                                            <span className="text-muted">Order Type:</span>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <span className="fw-semibold">{selectedTicket?.order?.ordersType}</span>
                                                        </div>

                                                        <div className="col-6">
                                                            <span className="text-muted">Pickup:</span>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <span className="fw-semibold">{selectedTicket?.order?.pickupOption}</span>
                                                        </div>

                                                        <div className="col-6">
                                                            <span className="text-muted">Payment:</span>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <span className="fw-semibold">{selectedTicket?.order?.paymentMode}</span>
                                                        </div>

                                                        <div className="col-6">
                                                            <span className="text-muted">Status:</span>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <span className="fw-semibold">{selectedTicket?.order?.paymentStatus}</span>
                                                        </div>

                                                        <div className="col-6">
                                                            <span className="text-muted">Ready Time:</span>
                                                        </div>
                                                        <div className="col-6 text-end">
                                                            <span className="fw-semibold">{selectedTicket?.order?.readyTime}</span>
                                                        </div>
                                                    </div>

                                                    {/* Financial Summary */}
                                                    <div className="mt-2 pt-2 border-top">
                                                        <div className="row g-1 small">
                                                            <div className="col-6">
                                                                <span className="text-muted">Subtotal:</span>
                                                            </div>
                                                            <div className="col-6 text-end">
                                                                <span className="fw-semibold">
                                                                    ₹{parseFloat(selectedTicket?.order?.totalTaxable?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6">
                                                                <span className="text-muted">Tax:</span>
                                                            </div>
                                                            <div className="col-6 text-end">
                                                                <span className="fw-semibold">
                                                                    ₹{parseFloat(selectedTicket?.order?.totalTaxAmount?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6">
                                                                <span className="text-muted">Platform Fee:</span>
                                                            </div>
                                                            <div className="col-6 text-end">
                                                                <span className="fw-semibold">
                                                                    ₹{parseFloat(selectedTicket?.order?.platformFees?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6">
                                                                <span className="text-muted">Delivery:</span>
                                                            </div>
                                                            <div className="col-6 text-end">
                                                                <span className="fw-semibold">
                                                                    ₹{parseFloat(selectedTicket?.order?.deliveryCharges?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6">
                                                                <span className="text-muted">Discount:</span>
                                                            </div>
                                                            <div className="col-6 text-end">
                                                                <span className="fw-semibold text-danger">
                                                                    -₹{parseFloat(selectedTicket?.order?.totalDiscount?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>

                                                            <div className="col-6 border-top pt-1">
                                                                <span className="fw-semibold">Total:</span>
                                                            </div>
                                                            <div className="col-6 text-end border-top pt-1">
                                                                <span className="fw-bold text-success">
                                                                    ₹{parseFloat(selectedTicket?.order?.grandTotal?.$numberDecimal || 0).toFixed(2)}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Action Button */}
                                    <Button
                                        variant={selectedTicket?.status === "OPEN" ? "outline-danger" : "outline-success"}
                                        size="sm"
                                        className="w-100 mt-3"
                                        onClick={() => handleUpdateStatus(selectedTicket)}
                                        disabled={loadingRowId === selectedTicket?._id}
                                    >
                                        {loadingRowId === selectedTicket?._id ? (
                                            <Spinner size="sm" animation="border" />
                                        ) : selectedTicket?.status === "OPEN" ? (
                                            "Close Ticket"
                                        ) : (
                                            "Reopen Ticket"
                                        )}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                </Modal>
            </div>
        </React.Fragment>
    );
};

export default CustomerSupportMgt;



// ======= Old Code =======
// import React, { useEffect, useState } from 'react';
// import { 
//   Badge, 
//   Button, 
//   Col, 
//   Container, 
//   Form, 
//   Modal, 
//   Row, 
//   Spinner, 
//   Table 
// } from 'react-bootstrap';
// import axios from '../../api/axios';
// import { toast } from 'react-toastify';
// import DatePicker from 'react-datepicker';
// import 'react-datepicker/dist/react-datepicker.css';
// import Header from '../../layouts/Header';
// import ReusableTable from '../../components/ReusableTable';

// const CustomerSupportMgt = () => {
//     const currentSkin = localStorage.getItem('skin-mode') ? 'dark' : '';
//     const [skin, setSkin] = useState(currentSkin);

//     const accessToken = localStorage.getItem('authToken');
//     const [ticketList, setTicketList] = useState([]);
//     const [filteredData, setFilteredData] = useState([]);
//     const [searchText, setSearchText] = useState('');
//     const [selectedStatus, setSelectedStatus] = useState("OPEN");
//     const [startDate, setStartDate] = useState(null);
//     const [endDate, setEndDate] = useState(null);
//     const [loading, setLoading] = useState(false);
//     const [error, setError] = useState('');
//     const [loadingRowId, setLoadingRowId] = useState(null);
//     const [page, setPage] = useState(1);
//     const [rowsPerPage, setRowsPerPage] = useState(10);
//     const [showModal, setShowModal] = useState(false);
//     const [viewModal, setViewModal] = useState(false)
//     const [selectedTicket, setSelectedTicket] = useState({});

//     const columns = [
//         {
//             header: "Sr.No",
//             accessor: "serialNumber", // now it uses the injected field
//             render: (value) => <span>{value}</span>,
//         },          
          
//         {
//             header: "Ticket No",
//             accessor: "ticket_no",
//             render: (value) => <div className="fs-7">{value || "--------"}</div>,
//         },
//         {
//             header: "Raised On",
//             accessor: "createdAt",
//             render: (value) =>
//                 value ? (
//                     <div className="fs-7">
//                         {new Date(value).toLocaleDateString("en-US", {
//                             year: "numeric",
//                             month: "numeric",
//                             day: "numeric",
//                         })}
//                         <br />
//                         {new Date(value).toLocaleTimeString("en-US", {
//                             hour: "2-digit",
//                             minute: "2-digit",
//                             hour12: true,
//                         })}
//                     </div>
//                 ) : (
//                 "--------"
//             ),
//         },
//         {
//             header: "Name",
//             accessor: null,
//             render: (_, row) => {
//                 console.log("Row Data (Name Column):", row); // ✅ Log here
//                 return (
//                   <div>
//                     <div className="fs-7 fw-semibold text-success">
//                       {row?.name || "--------"}
//                     </div>
//                   </div>
//                 );
//               },
//         },
//         {
//             header: "Support Type",
//             accessor: "support_type",
//             render: (value) => <div className="fs-7">{value || "--------"}</div>,
//         },
//         {
//             header: "Subject",
//             accessor: "subject",
//             render: (value) => (
//                 <div className="fs-7">{value ? value.toUpperCase() : "--------"}</div>
//             ),
//         },
//         {
//             header: "Status",
//             accessor: "status",
//             render: (value) => (
//                 <p
//                     className={`${
//                         value === "OPEN" ? "text-success" : "text-danger"
//                     } fs-7 fw-bold mt-3`}
//                 >
//                     {value}
//                 </p>
//             ),
//         },
//         {
//             header: "Actions",
//             accessor: null,
//             render: (_, row) => (
//                 <div className="d-flex align-items-center gap-2 w-100">
//                     <Button
//                         size="sm"
//                         className="w-100 fw-semibold"
//                         style={{
//                             borderRadius: "20px",
//                             fontSize: "11px",
//                             boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                             transition: "background-color 0.3s, transform 0.3s",
//                             transform: loadingRowId === row._id ? "scale(1)" : "scale(1.05)",
//                         }}
//                         onClick={() => handleUpdateStatus(row)}
//                         disabled={loadingRowId === row._id}
//                         variant={
//                         loadingRowId === row._id
//                             ? "secondary"
//                             : row?.status === "OPEN"
//                             ? "danger"
//                             : "success"
//                         }
//                     >
//                         {loadingRowId === row._id ? "Loading..." : row?.status === "OPEN" ? "Close Ticket" : "Open Ticket"}
//                     </Button>
        
//                     <Button
//                         size="sm"
//                         className="w-100 fw-semibold"
//                         style={{
//                             borderRadius: "20px",
//                             fontSize: "11px",
//                             backgroundColor: "#1E9CED",
//                             color: "white",
//                             boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
//                             transition: "background-color 0.3s, transform 0.3s",
//                             transform: "scale(1.05)",
//                             border: "none",
//                         }}
//                         onClick={() => handleViewDetails(row)}
//                     >
//                         View Details
//                     </Button>
//                 </div>
//             ),
//         },
//     ];

//     const handleSubmit = async (e) => {
//         const adjustedStartDate = new Date(startDate?.setHours(23, 59, 59, 999));
//         const adjustedEndDate = new Date(endDate?.setHours(23, 59, 59, 999));

//         setLoading(true);
//         await getTickets(selectedStatus, adjustedStartDate, adjustedEndDate);
//         toast.success("Data Fetched Successfully", {
//             position: "top-right",
//             autoClose: 3000,
//             theme: "colored",
//         });
//         setLoading(false);
//     }

//     const getTickets = async (status, startDate, endDate) => {
//         try {
//             const headers = {
//                 Authorization: `Bearer ${accessToken}`,
//             };

//             const updatedStartDate = startDate ? startDate.toISOString().split("T")[0] : null;
//             const updatedEndDate = endDate ? endDate.toISOString().split("T")[0] : null;

//             let url = `customer-support/get-tickets?`

//             if (status) {
//                 url += `status=${status}&`
//             }

//             if (updatedStartDate) {
//                 url += `startDate=${updatedStartDate}&`
//             }

//             if (updatedEndDate) {
//                 url += `endDate=${updatedEndDate}&`
//             }

//             url = url.endsWith('&') ? url.slice(0, -1) : url;

//             const response = await axios.get(url, { headers });

//             const tableDataWithIndex = response?.data?.data?.tickets?.map((item, idx) => ({
//                 ...item,
//                 serialNumber: idx + 1,
//             }));

//             setTicketList(tableDataWithIndex);
//             setFilteredData(tableDataWithIndex);
//         } catch (error) {
//         if (error.response && error.response?.data?.status === false) {
//             let errorMessage = error.response.data.error;
//             toast.error(errorMessage, {
//                 position: "top-right",
//                 autoClose: 3000,
//                 theme: "colored",
//             });
//         } else if (error.response && error.response?.data?.status === 404) {
//             let errorMessage = error.response.data.error;
//             toast.error(errorMessage, {
//                 position: "top-right",
//                 autoClose: 3000,
//                 theme: "colored",
//             });
//         }
//         }
//     };

//     const handleSearchChange = (e) => {
//         setSearchText(e.target.value);
//     }

//     const handleEndDateChange = (date) => {
//         if (date && startDate && date < startDate) {
//             setError("End date cannot be earlier than start date")
//         } else {
//             setError("");
//             setEndDate(date);
//         }
//     }

//     const handleUpdateStatus = async (data) => {
//         const newStatus = data?.status === "OPEN" ? "CLOSED" : "OPEN";
//         setLoadingRowId(data?._id);
//         try {
//             const headers = {
//                 Authorization: `Bearer ${accessToken}`,
//             };
//             const response = await axios.put(`customer-support/user-tickets`, {
//                 _id: data?._id,
//                 status: newStatus
//             }, { headers });

//             if (response?.data?.status === true) {
//                 toast.success(response?.data?.data?.message, {
//                     position: "top-right",
//                     autoClose: 3000,
//                     theme: "colored",
//                 })
//                 getTickets(selectedStatus, startDate, endDate)
//             }
//         } catch (error) {
//             if (error?.response) {
//                 let errorMessage = '';
//                 if (error.response?.status === 406) {
//                     errorMessage = error?.response?.data?.error?.message || error?.response?.data?.error
//                 }
//                 else if (error.response?.status === 412) {
//                     errorMessage = error?.response?.data?.data?.message;
//                 } else if (error.response?.status === 500) {
//                     errorMessage = error?.response?.data?.error
//                 }

//                 toast.error(errorMessage, {
//                     position: "top-right",
//                     autoClose: 3000,
//                     theme: "colored",
//                 });
//             }
//         } finally {
//             setLoadingRowId(null);
//         }
//     };

//     const handleViewDetails = (data) => {
//         // setShowModal(true);
//         console.log("Data :", data);
        
//         setViewModal(true);
//         setSelectedTicket(data);
//     }

//     const handleCloseViewModal = () => {
//         setViewModal(false);
//     }

//     const handleCloseModal = () => {
//         setShowModal(false);
//     }

//     const handlePageChange = (page) => {
//         setPage(page);
//     };
  
//     const handleRowsPerPageChange = (rowsPerPage) => {
//         setPage(1);
//         setRowsPerPage(rowsPerPage);
//     };

//     const getPaginatedData = () => {
//         const startIndex = (page - 1) * rowsPerPage;
//         const endIndex = startIndex + rowsPerPage;
//         return filteredData.slice(startIndex, endIndex);
//     };

//     // const getTicketMessages = async() => {
//     //     try {
//     //         const headers = {
//     //             Authorization : `Bearer ${accessToken}`
//     //         }
//     //         const response = await axios.get(`customer-support/68db6eff2037650ea462c8c8/conversation`, { headers });
//     //         console.log("Response of message :", response);
            
//     //     } catch (error) {
//     //         console.log("Error In Getting Messages :", error);
//     //     }
//     // }

//     // useEffect(() => {
//     //     getTicketMessages()
//     // }, []);

//     useEffect(() => {
//         const now = new Date();
//         const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
//         const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        
//         const start = new Date(firstOfMonth.setHours(23, 59, 59, 999));
//         const end = new Date(currentDate.setHours(23, 59, 59, 999));

//         setStartDate(start);
//         setEndDate(end);
//         getTickets(selectedStatus, start, end);
//     }, []);

//     useEffect(() => {
//         const result = ticketList.filter((item) => {
//             return (
//                 item?.user_id?.name?.toLowerCase().match(searchText.toLocaleLowerCase()) ||
//                 item?.support_type?.toLowerCase().match(searchText.toLocaleLowerCase()) ||
//                 item?.ticket_no?.toLowerCase().match(searchText.toLocaleLowerCase())
//             )
//         });
//         setFilteredData(result)
//     }, [searchText]);

//     return (
//         <React.Fragment>
//             <Header onSkin={setSkin} />
//             <div className="main main-app p-3 p-lg-4">
//                 <Container fluid className="shadow-sm bg-white py-3">
//                 <Row className="mb-3">
//                     <Col>
//                         <h5 className="fw-semibold">CUSTOMER SUPPORT MANAGEMENT</h5>
//                     </Col>
//                     <hr className='mb-0' />
//                 </Row>

//                 {/* Filters */}
//                 <Row className="mb-4 g-3">
//                     <Col md={3}>
//                     <Form.Group>
//                         <Form.Label className="fw-semibold">Select Status</Form.Label>
//                         <Form.Select
//                             value={selectedStatus}
//                             onChange={(e) => setSelectedStatus(e.target.value)}
//                         >
//                             <option value="all">ALL</option>
//                             <option value="OPEN">OPEN</option>
//                             <option value="CLOSED">CLOSED</option>
//                         </Form.Select>
//                     </Form.Group>
//                     </Col>
                    
//                     <Col md={2}>
//                     <Form.Group>
//                         <Row>
//                             <Form.Label className="fw-semibold">Start Date</Form.Label>
//                         </Row>
//                         <DatePicker
//                             selected={startDate}
//                             onChange={(date) => setStartDate(date)}
//                             dateFormat="dd-MM-yyyy"
//                             placeholderText="Select Start Date"
//                             className="form-control"
//                         />
//                     </Form.Group>
//                     </Col>
                    
//                     <Col md={2}>
//                     <Form.Group>
//                         <Row>
//                             <Form.Label className="fw-semibold">End Date</Form.Label>
//                         </Row>
//                         <DatePicker
//                             selected={endDate}
//                             onChange={handleEndDateChange}
//                             dateFormat="dd-MM-yyyy"
//                             placeholderText="Select End Date"
//                             className="form-control"
//                         />
//                         {error && <div className="text-danger small mt-1">{error}</div>}
//                     </Form.Group>
//                     </Col>
                    
//                     <Col md={3} className="text-end">
//                         <Row>
//                             <Form.Label className="text-white">.</Form.Label>
//                         </Row>
//                     <Button 
//                         variant="primary" 
//                         className="w-100"
//                         onClick={handleSubmit}
//                         disabled={loading}
//                     >
//                         {loading ? <Spinner size="sm" animation="border" /> : 'Submit'}
//                     </Button>
//                     </Col>
//                 </Row>

//                 {/* Tickets List */}
//                 <Row>
//                     <Col>
//                     <ReusableTable
//                         title="TICKETS LIST"
//                         columns={columns}
//                         data={getPaginatedData()}
//                         rowKey="serialNumber"
//                         loading={loading}
//                         currentPage={page}
//                         recordsPerPage={rowsPerPage}
//                         onPageChange={handlePageChange}
//                         totalPages={Math.ceil(filteredData.length / rowsPerPage)}
//                         onRecordsPerPageChange={handleRowsPerPageChange}


//                         // pagination
//                         // paginationServer
//                         // paginationTotalRows={filteredData.length}
//                         // paginationPerPage={pagination.rowsPerPage}
//                         // onChangeRowsPerPage={handleRowsPerPageChange}
//                         // onChangePage={handlePageChange}
//                         // subHeader
//                         // subHeaderComponent={
//                         //   <input
//                         //     type="text"
//                         //     placeholder="Search..."
//                         //     className="form-control w-25"
//                         //     value={searchText}
//                         //     onChange={handleSearchChange}
//                         //   />
//                         // }
//                         // striped
//                         // highlightOnHover
//                     />
//                     </Col>
//                 </Row>
//                 </Container>

//                 {/* Ticket Details Modal */}
//                 <Modal
//                     show={viewModal}
//                     onHide={handleCloseViewModal}
//                     size="xl"
//                     centered
//                 >
//                     <Modal.Header closeButton>
//                         <Modal.Title className="w-100 text-center">SUPPORT REQUEST DETAILS</Modal.Title>
//                     </Modal.Header>
//                     <Modal.Body>
//                         {selectedTicket ? (
//                         <div className="p-3">
//                             <Row className="mb-2">
//                             <h6>USER DETAILS -</h6>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>User Name :</h6>
//                                 <p>{selectedTicket?.user?.name}</p>
//                             </Col>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Mobile Number :</h6>
//                                 <p>
//                                 {selectedTicket?.user?.mobile?.includes("_deleted_")
//                                     ? `${selectedTicket?.user?.mobile.split("_deleted_")[0]} [Deleted]`
//                                     : selectedTicket?.user?.mobile}
//                                 </p>
//                             </Col>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Email :</h6>
//                                 <p>
//                                 {selectedTicket?.user?.email?.includes("_deleted_")
//                                     ? `${selectedTicket?.user?.email.split("_deleted_")[0]} [Deleted]`
//                                     : selectedTicket?.user?.email
//                                 }
//                                 </p>
//                             </Col>
//                             </Row>
                            
//                             <hr />

//                             <Row className="mb-2">
//                             <h6>TICKET DETAILS -</h6>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Ticket No :</h6>
//                                 <p>{selectedTicket?.ticket_no}</p>
//                             </Col>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Status :</h6>
//                                 <p
//                                 style={{ color: selectedTicket?.status === "OPEN" ? "green" : "red" }}
//                                 >
//                                 {selectedTicket?.status}
//                                 </p>
//                             </Col>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Created On :</h6>
//                                 <p>
//                                 {new Date(selectedTicket?.createdAt).toLocaleDateString('en-US', {
//                                     year: 'numeric',
//                                     month: 'long',
//                                     day: 'numeric',
//                                 })}{" / "}
//                                 {new Date(selectedTicket?.createdAt).toLocaleTimeString('en-US', {
//                                     hour: 'numeric',
//                                     minute: 'numeric',
//                                     hour12: true,
//                                 })}
//                                 </p>
//                             </Col>
//                             </Row>
                            
//                             <Row>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Status Updated On :</h6>
//                                 <p>
//                                 {
//                                     selectedTicket?.updatedAt !== selectedTicket?.createdAt ? (
//                                     <>
//                                         {new Date(selectedTicket?.updatedAt).toLocaleDateString('en-US', {
//                                         year: 'numeric',
//                                         month: 'long',
//                                         day: 'numeric',
//                                         })}
//                                         {" / "}
//                                         {new Date(selectedTicket?.updatedAt).toLocaleTimeString('en-US', {
//                                         hour: 'numeric',
//                                         minute: 'numeric',
//                                         hour12: true,
//                                         })}
//                                     </>
//                                     ) : (
//                                     "Yet To Be Updated"
//                                     )
//                                 }
//                                 </p>
//                             </Col>
//                             <Col xs={4}>
//                                 <h6 className='text-secondary'>Support Type :</h6>
//                                 <p>{selectedTicket?.support_type}</p>
//                             </Col>
//                             <Col xs={4} md={4}>
//                                 <h6 className='text-secondary'>Subject :</h6>
//                                 <p>{selectedTicket?.subject}</p>
//                             </Col>
//                             </Row>

//                             <Row>
//                             <Col xs={12} md={12}>
//                                 <h6 className='text-secondary'>Message (Description) :</h6>
//                                 <p>{selectedTicket?.message}</p>
//                             </Col>
//                             </Row>

//                             { selectedTicket?.order !== null ? (
//                             <hr /> ) : null }

//                             <Row className="mb-2">
//                             {
//                                 selectedTicket?.order !== null && 
//                                 <>
//                                 <h6>ORDER DETAILS -</h6>
//                                 <Col xs={4}>
//                                     <h6 className='text-secondary'>Order No :</h6>
//                                     <p>{`${selectedTicket?.order?.order_sequence}${selectedTicket?.order?.order_no}`}</p>
//                                 </Col>
//                                 </>
//                             }

//                             {
//                                 selectedTicket?.order !== null && 
//                                 <Col xs={4}>
//                                 <h6 className='text-secondary'>Serial No :</h6>
//                                 <p>{selectedTicket?.order?.serialNo}</p>
//                                 </Col>
//                             }

//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Order Type :</h6>
//                                 <p >{selectedTicket?.order?.ordersType}</p>
//                                 </Col>
//                             }

//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>User :</h6>
//                                 <p>{selectedTicket?.order?.kioskUser ? "Kiosk User" : "App User"}</p>
//                                 </Col>
//                             }
//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Coupon Code :</h6>
//                                 <p className='fw-semibold' style={{ color: 'purple' }}>{selectedTicket?.order?.coupon ? selectedTicket?.order?.coupon?.couponCode : "No Coupon Applied"}</p>
//                                 </Col>
//                             }
//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Outlet :</h6>
//                                 <p>{selectedTicket?.order?.outlet}</p>
//                                 </Col>
//                             }

//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Payment Status :</h6>
//                                 <p className='text-success fw-semibold'>{selectedTicket?.order?.paymentStatus}</p>
//                                 </Col>
//                             }
//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Order Status :</h6>
//                                 <p className='text-warning'>{selectedTicket?.order?.orderStatus}</p>
//                                 </Col>
//                             }
//                             {
//                                 selectedTicket?.order !== null && <Col xs={4}>
//                                 <h6 className='text-secondary'>Grand Total :</h6>
//                                 <p >₹. {selectedTicket?.order?.grandTotal.toFixed(2)}</p>
//                                 </Col>
//                             }
//                             </Row>

//                             { selectedTicket?.support_type === "PAYMENT-REFUND" ? (
//                             <hr /> ) : null }

//                             { selectedTicket?.support_type === "PAYMENT-REFUND" ? (
//                             <>
//                                 <Row className="mb-2">
//                                 <h6>ORDERD ITEMS -</h6>
//                                 <Table striped bordered hover responsive>
//                                     <thead>
//                                         <tr>
//                                         <th className="fw-semibold">Product</th>
//                                         <th className="text-center fw-semibold">Quantity</th>
//                                         <th className="text-center fw-semibold">Price</th>
//                                         </tr>
//                                     </thead>
//                                     <tbody>
//                                         {selectedTicket?.order?.order_items?.map((item) => (
//                                             <tr key={item?._id}>
//                                                 <td>{item?.product?.name}
//                                                     {item?.addons?.length > 0 ? (
//                                                     <ul style={{ fontSize: '14px' }}>
//                                                         {item?.addons?.map((addon) => (
//                                                         <li key={addon?._id}>
//                                                             {addon?.selectedValue} (₹{addon?.offerPrice})
//                                                         </li>
//                                                         ))}
//                                                     </ul>
//                                                     ) : (
//                                                     ""
//                                                     )}
//                                                 </td>
//                                                 <td className="text-center">{item?.quantity}</td>
//                                                 <td className="text-end">
//                                                     ₹{item?.rate?.toFixed(2)}
//                                                     <ul style={{ fontSize: '14px', listStyleType: 'none' }}>
//                                                         {item?.addons?.map((addon) => (
//                                                         <li key={addon?._id}>
//                                                             + (₹{addon?.offerPrice})
//                                                         </li>
//                                                         ))}
//                                                     </ul>
//                                                 </td>
//                                             </tr>
//                                         ))}
//                                         {/* Other Charges */}
//                                         { selectedTicket?.order?.platformFees !== 0 ? (
//                                             <tr>
//                                             <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>
//                                                 Platform Fees:
//                                             </td>
//                                             <td style={{ fontSize: '14px' }} className="text-end">
//                                                 ₹ {Number(selectedTicket?.order?.platformFees).toFixed(2)}
//                                             </td>
//                                             </tr>
//                                         ) : null }
                                        
//                                         { selectedTicket?.order?.packingCharges !== 0 && null ? (
//                                             <tr>
//                                             <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Packing Fees:</td>
//                                             <td style={{ fontSize: '14px' }} className="text-end">
//                                                 ₹ {selectedTicket?.order?.packingCharges?.toFixed(2)}
//                                             </td>
//                                             </tr>
//                                         ) : null }

//                                         { selectedTicket?.order?.deliveryCharges !== 0 && null ? (
//                                             <tr>
//                                             <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Delivery Charges:</td>
//                                             <td style={{ fontSize: '14px' }} className="text-end">
//                                                 ₹ {selectedTicket?.order?.deliveryCharges?.toFixed(2)}
//                                             </td>
//                                             </tr>
//                                         ) : null }

//                                         <tr>
//                                             <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Total Tax Amount:</td>
//                                             <td style={{ fontSize: '14px' }} className="text-end">
//                                             ₹ {selectedTicket?.order?.totalTaxAmount?.toFixed(2)}
//                                             </td>
//                                         </tr>

//                                         <tr>
//                                             <td colSpan={2} className="text-end" style={{ fontSize: '14px' }}>Total Discount:</td>
//                                             <td style={{ fontSize: '14px' }} className="text-end">
//                                             ₹ {selectedTicket?.order?.totalDiscount?.toFixed(2)}
//                                             </td>
//                                         </tr>

//                                         {/* Calculate grand total */}
//                                         <tr>
//                                             <td colSpan={2} className="text-end"><strong>Grand Total:</strong></td>
//                                             <td className="text-end text-success fw-semibold">
//                                                 ₹. {selectedTicket?.order?.grandTotal.toFixed(2)}
//                                             </td>
//                                         </tr>
//                                     </tbody>
//                                 </Table>
//                                 </Row>

//                                 <Row className="mb-1">
//                                 <Col xs={12}>
//                                 <h6>Order Status Log :</h6>
//                                 <Table striped bordered hover responsive>
//                                     <thead>
//                                     <tr>
//                                         <th className="fw-semibold">Order Status</th>
//                                         <th className="fw-semibold">Time</th>
//                                         <th className="fw-semibold">Date</th>
//                                     </tr>
//                                     </thead>
//                                     <tbody>
//                                     {selectedTicket?.order?.order_status_logs?.length > 0 ? (
//                                         selectedTicket?.order?.order_status_logs?.map((log) => (
//                                         <tr key={log._id}>
//                                             <td>
//                                             <Badge
//                                                 bg={
//                                                 log?.orderStatus === "COMPLETED"
//                                                     ? "success"
//                                                     : log?.orderStatus === "DELIVERED"
//                                                     ? "primary"
//                                                     : log?.orderStatus === "CONFIRMED"
//                                                     ? "secondary"
//                                                     : log?.orderStatus === "READY-TO-PICK"
//                                                     ? "warning"
//                                                     : log?.orderStatus === "CANCELLED"
//                                                     ? "danger"
//                                                     : "dark"
//                                                 }
//                                             >
//                                                 {log?.orderStatus}
//                                             </Badge>
//                                             </td>
//                                             <td>
//                                             {new Date(log?.createdAt).toLocaleTimeString('en-US', {
//                                                 hour: 'numeric',
//                                                 minute: 'numeric',
//                                                 hour12: true,
//                                             })}
//                                             </td>
//                                             <td>
//                                             {new Date(log?.createdAt).toLocaleDateString('en-US', {
//                                                 year: 'numeric',
//                                                 month: 'long',
//                                                 day: 'numeric',
//                                             })}                                
//                                             </td>
//                                         </tr>
//                                         ))
//                                     ) : (
//                                         <tr>
//                                         <td colSpan={3} className="text-center">
//                                             No order status logs available.
//                                         </td>
//                                         </tr>
//                                     )}
//                                     </tbody>
//                                 </Table>
//                                 </Col>
//                                 </Row>
//                             </>
//                             ) : null }
//                         </div>
//                         ) : (
//                         <p>No details available.</p>
//                         )}
//                     </Modal.Body>
//                     <Modal.Footer>
//                         <Button variant="secondary" onClick={handleCloseViewModal}>
//                         Close
//                         </Button>
//                     </Modal.Footer>
//                 </Modal>
//             </div>
//         </React.Fragment>
//     );
// };

// export default CustomerSupportMgt;